import { useEffect, useRef, useState } from "react";
import { AlertCircle, Download } from "lucide-react";
import { Button, ButtonLink } from "../components/ui/Button";
import { ebookPdf } from "../content";
import { company } from "../data/site";
import { cx } from "../lib/cx";
import { submitHubSpotForm } from "../lib/hubspot";

// The old site's HubSpot e-book form, field for field.
const fields = [
  { name: "firstname", label: "First name", autoComplete: "given-name" },
  { name: "lastname", label: "Last name", autoComplete: "family-name" },
  { name: "email", label: "Email", type: "email", autoComplete: "email" },
  { name: "jobtitle", label: "Job title", autoComplete: "organization-title" },
];

// Remembered in this browser so returning visitors skip the form.
const unlockedKey = "xeratec-ebook-unlocked";
const remember = () => {
  try {
    localStorage.setItem(unlockedKey, "1");
  } catch {}
};
const remembered = () => {
  try {
    return localStorage.getItem(unlockedKey) === "1";
  } catch {
    return false;
  }
};

const inputClass = (invalid) =>
  cx(
    "mt-2 w-full rounded-brand border bg-white px-4 py-3 text-base text-ink transition outline-none focus:ring-4",
    invalid
      ? "border-[#b3261e] focus:border-[#b3261e] focus:ring-[#b3261e]/15"
      : "border-line-strong hover:border-subtle focus:border-blue focus:ring-blue/20",
  );

function DownloadLink({ className }) {
  return (
    <ButtonLink href={ebookPdf} download className={className}>
      <Download size={18} aria-hidden="true" />
      Download the e-book
      <span className="sr-only"> (PDF, 16 pages)</span>
    </ButtonLink>
  );
}

/**
 * The e-book form: name, email and job title go to the HubSpot e-book form
 * (as on the old site), then the download unlocks. If HubSpot can't be
 * reached, the download unlocks anyway — the visitor shouldn't pay for it.
 */
export default function EbookForm() {
  const formRef = useRef(null);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");

  useEffect(() => {
    if (remembered()) setStatus("unlocked");
  }, []);

  function validate(form) {
    const found = {};
    for (const { name, label } of fields) {
      const input = form.elements[name];
      if (!input.value.trim())
        found[name] = `Enter your ${label.toLowerCase()}.`;
      else if (!input.checkValidity())
        found[name] = "Enter an email address like name@company.com.";
    }
    return found;
  }

  async function submit(e) {
    e.preventDefault();
    const form = e.currentTarget;
    const found = validate(form);
    setErrors(found);
    const first = Object.keys(found)[0];
    if (first) {
      form.elements[first].focus();
      return;
    }
    setStatus("sending");
    const values = Object.fromEntries(
      fields.map(({ name }) => [name, form.elements[name].value.trim()]),
    );
    try {
      await submitHubSpotForm("ebook", values);
    } catch {
      // Still let them have it; the lead is lost, not the reader.
    }
    remember();
    setStatus("unlocked");
  }

  if (status === "unlocked")
    return (
      <div role="status" className="text-center">
        <h3 className="font-display text-[28px] text-ink uppercase">
          Your copy is ready.
        </h3>
        <p className="mx-auto mt-2 mb-6 max-w-[420px] text-muted">
          Enjoy the read. Questions about anything in it? Email{" "}
          <a
            href={`mailto:${company.email}`}
            className="font-bold text-blue-deep underline underline-offset-2"
          >
            {company.email}
          </a>
          .
        </p>
        <DownloadLink />
      </div>
    );

  return (
    <form ref={formRef} onSubmit={submit} noValidate>
      <div className="grid gap-5 sm:grid-cols-2">
        {fields.map(({ name, label, ...props }) => {
          const invalid = Boolean(errors[name]);
          return (
            <div key={name}>
              <label
                htmlFor={`ebook-${name}`}
                className="block text-[15px] font-bold text-ink"
              >
                {label}
              </label>
              <input
                id={`ebook-${name}`}
                name={name}
                required
                maxLength={160}
                aria-invalid={invalid || undefined}
                aria-describedby={invalid ? `ebook-${name}-error` : undefined}
                className={inputClass(invalid)}
                {...props}
              />
              {invalid && (
                <p
                  id={`ebook-${name}-error`}
                  className="mt-2 flex items-center gap-1.5 text-sm font-bold text-[#b3261e]"
                >
                  <AlertCircle size={16} aria-hidden="true" />
                  {errors[name]}
                </p>
              )}
            </div>
          );
        })}
      </div>
      <div className="mt-7 flex flex-wrap items-center gap-x-6 gap-y-3">
        <Button type="submit" disabled={status === "sending"}>
          <Download size={18} aria-hidden="true" />
          {status === "sending" ? "One moment…" : "Get the e-book"}
        </Button>
        <p className="max-w-[40ch] text-sm text-muted">
          By submitting, you agree to the{" "}
          <a
            href="#privacy-policy"
            className="font-bold text-blue-deep underline underline-offset-2"
          >
            privacy policy
          </a>{" "}
          below.
        </p>
      </div>
    </form>
  );
}
